import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import RedeemService from '../../../services/redeem.service';
import { buildLoginUrlWithReturn } from '../../../utils/returnTo';

const s = {
  title: { fontSize: 18, fontWeight: 600, marginBottom: 6, color: '#e2e2f0' },
  sub: { fontSize: 13, color: '#888', marginBottom: 24 },
  label: { display: 'block', fontSize: 12, fontWeight: 500, color: '#aaa', marginBottom: 6 },
  input: {
    width: '100%',
    background: '#0f0f1a',
    border: '1px solid #2a2a40',
    borderRadius: 10,
    padding: '12px 16px',
    fontSize: 14,
    color: '#e2e2f0',
    outline: 'none',
    boxSizing: 'border-box',
    marginBottom: 16,
  },
  error: { fontSize: 11, color: '#f87171', marginTop: 6 },
  btnPrimary: {
    width: '100%',
    background: '#7c3aed',
    color: 'white',
    border: 'none',
    borderRadius: 10,
    padding: '13px 0',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 20,
    textAlign: 'center',
  },
  btnSecondary: {
    width: '100%',
    background: 'transparent',
    border: '1px solid #2a2a40',
    color: '#888',
    borderRadius: 10,
    padding: '11px 0',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 10,
    textAlign: 'center',
  },
  loginBanner: {
    background: 'rgba(124,58,237,0.1)',
    border: '1px solid rgba(124,58,237,0.3)',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  loginBannerText: { fontSize: 13, color: '#ccc', marginBottom: 12 },
  loginLink: {
    fontSize: 13,
    fontWeight: 600,
    color: '#a78bfa',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
};

export default function Step2Email({ wizard }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hint, setHint] = useState(null);
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    const email = wizard.email.trim();
    const companyName = wizard.companyName.trim();
    if (!email || !email.includes('@')) { setError('Ingresa un correo valido.'); return; }
    if (!companyName) { setError('Ingresa el nombre de tu empresa.'); return; }
    setLoading(true);
    setError('');
    setHint(null);

    try {
      const data = await RedeemService.activateCoupon({
        email,
        code: wizard.couponCode,
        company_name: companyName,
        channel: wizard.selectedChannel,
        integration_id: wizard.integrationId,
      });
      wizard.setRedemptionData(data);

      // Keys PROPIAS del redeem: OrganizationContext borra 'currentAccountId'
      // cuando no puede resolver la cuenta (usuario recién activado, sin sesión),
      // así que el redeem no puede depender de ella. Estas keys nadie las toca.
      if (data.account_id) {
        localStorage.setItem('redeem_account_id', data.account_id);
        localStorage.setItem('redeem_company_name', companyName);
        if (data.organization_id) {
          localStorage.setItem('redeem_organization_id', data.organization_id);
        }
        // El workflow_id se necesita en el "Activar y entrar" (updateChannel) para
        // cablear el canal — y el redemptionData se pierde si se vino del enlace del correo.
        if (Array.isArray(data.workflow_ids) && data.workflow_ids.length) {
          localStorage.setItem('redeem_workflow_ids', JSON.stringify(data.workflow_ids));
        }
      }

      // Save org account so OrganizationContext boots into the right account on reload
      if (data.account_id) {
        localStorage.setItem('currentAccountId', data.account_id);
        localStorage.setItem('currentAccountType', 'ORGANIZATION');
        localStorage.setItem('currentContext', JSON.stringify({
          id: data.organization_id,
          type: 'ORGANIZATION',
          name: companyName,
          account_id: data.account_id,
          organization_id: data.organization_id,
        }));
        // currentAccount also needs organization_id so listOrganizationProjects gets the right id
        localStorage.setItem('currentAccountMeta', JSON.stringify({
          id: data.account_id,
          type: 'ORGANIZATION',
          organization_id: data.organization_id,
          name: companyName,
        }));
      }

      // La cuenta se crea sin verificar (email_verified=false). Le mandamos un
      // enlace de verificación al correo; el usuario debe hacer clic para continuar.
      setSent(true);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || {};
      const context = detail?.context || err?.response?.data?.context;
      if (status === 409 && context?.hint === 'login') {
        setHint('login');
      } else {
        setError('Error al activar. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <div>
        <div style={s.title}>Revisá tu correo</div>
        <div style={s.sub}>
          Te enviamos un enlace de verificación a <strong>{wizard.email}</strong>.
          Hacé clic en el enlace para activar tu cuenta y continuar.
        </div>
        <button
          style={s.btnSecondary}
          onClick={async () => {
            try {
              await RedeemService.resendVerification({
                email: wizard.email.trim(),
                company_name: wizard.companyName.trim(),
                code: wizard.couponCode,
              });
            } catch (_) { /* no-op */ }
          }}
        >
          Reenviar enlace
        </button>
      </div>
    );
  }

  if (hint === 'login') {
    return (
      <div>
        <div style={s.title}>Tu correo electronico</div>
        <div style={s.loginBanner}>
          <div style={s.loginBannerText}>Ya tienes una cuenta con este correo.</div>
          <button style={s.loginLink} onClick={() => {
            const code = wizard.couponCode ? `?code=${encodeURIComponent(wizard.couponCode)}` : '';
            navigate(buildLoginUrlWithReturn(`/redeem${code}`));
          }}>
            Iniciar sesion
          </button>
        </div>
        <button style={s.btnSecondary} onClick={() => setHint(null)}>Atras</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <div style={s.title}>Tu correo electronico</div>
      <div style={s.sub}>Si ya tienes cuenta, asociaremos el template automaticamente.</div>

      <label style={s.label}>Correo electronico</label>
      <input
        type="email"
        value={wizard.email}
        onChange={(e) => wizard.setEmail(e.target.value)}
        placeholder="usuario@empresa.com"
        disabled={loading}
        style={{ ...s.input, opacity: loading ? 0.5 : 1 }}
      />

      <label style={s.label}>Nombre de la empresa</label>
      <input
        type="text"
        value={wizard.companyName}
        onChange={(e) => wizard.setCompanyName(e.target.value)}
        placeholder="Mi empresa"
        disabled={loading}
        style={{ ...s.input, marginBottom: 0, opacity: loading ? 0.5 : 1 }}
      />

      {error && <div style={s.error}>{error}</div>}

      <button
        type="submit"
        disabled={loading}
        style={{ ...s.btnPrimary, opacity: loading ? 0.6 : 1 }}
      >
        {loading ? 'Procesando...' : 'Continuar'}
      </button>
      <button
        type="button"
        onClick={() => wizard.goToStep(1)}
        disabled={loading}
        style={{ ...s.btnSecondary, opacity: loading ? 0.5 : 1 }}
      >
        Atras
      </button>
    </form>
  );
}
